
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { MarkerWithLabel } from '../measured-data-details.types';
import { MeasuredDataDetailsRegionService } from '../measured-data-details-region.service';

export interface OffsetState {
  timelineOffsetReference: number;
  hiddenRegion: any;
  hiddenRegionParams: any;
  waveSurferRegions: any;
  currentStartOffset: number;
  rsstStartMarker: MarkerWithLabel | null;
  wavesurfer: any;
  timelineElement?: HTMLElement | null;
  timelinePlugin?: any;
}

export interface TimelineViewState {
  currentStartOffset: number;
  timelineOffsetReference: number;
}

@Injectable({ providedIn: 'root' })
export class WaveTimelineService {
  private state: OffsetState | null = null;
  private readonly viewState = new BehaviorSubject<TimelineViewState>({
    currentStartOffset: 0,
    timelineOffsetReference: 0,
  });

  readonly viewState$ = this.viewState.asObservable();

  constructor(private regionService: MeasuredDataDetailsRegionService) {}

  init(state: OffsetState): void {
    this.state = { ...state, timelineElement: state.timelineElement ?? null };
    this.updateTimelinePluginOffset();
    this.emitViewState();
  }

  reset(): void {
    this.removeHiddenRegion();
    this.removeRsstStartMarker();
    this.state = null;
    this.viewState.next({ currentStartOffset: 0, timelineOffsetReference: 0 });
  }

  computeZoomStep(duration: number): number {
    if (duration < 120) {
      return 30;
    }
    if (duration < 300) {
      return 10;
    }
    if (duration < 600) {
      return 5;
    }
    return 1;
  }

  applyZoom(wavesurfer: any, currentValue: number, delta: number): number {
    let result = currentValue + delta;
    if (result < 0) {
      result = 0;
    }
    if (wavesurfer) {
      const duration = typeof wavesurfer.getDuration === 'function' ? wavesurfer.getDuration() : 0;
      const current = typeof wavesurfer.getCurrentTime === 'function' ? wavesurfer.getCurrentTime() : 0;
      wavesurfer.zoom(result);
      if (duration > 0) {
        const startClamp = Math.max(0, this.state?.currentStartOffset ?? 0);
        const clamped = Math.min(Math.max(current, startClamp), duration);
        wavesurfer.seekTo(clamped / duration);
      }
    }
    return result;
  }

  resetZoom(wavesurfer: any, currentStartOffset: number = 0): number {
    if (wavesurfer) {
      wavesurfer.zoom(0);
      const duration = typeof wavesurfer.getDuration === 'function' ? wavesurfer.getDuration() : 0;
      if (duration > 0) {
        const startClamp = Math.max(0, currentStartOffset ?? 0);
        const clamped = Math.min(Math.max(startClamp, 0), duration);
        wavesurfer.seekTo(clamped / duration);
      }
    }
    return 0;
  }

  adjustTimelineMargin(hiddenRegion: any, timelineElement: HTMLElement | null): void {
    if (!hiddenRegion || !timelineElement) {
      if (timelineElement) {
        const firstDivElement = timelineElement.querySelector('div') as HTMLElement;
        firstDivElement.style.marginLeft = '0px';
      }
      return;
    }

    // region が right 基準で描かれている場合と left 基準で描かれている場合の両方に対応
    let timelineLeftPercentage: number | null = null;
    const hiddenRegionRightString = hiddenRegion.element.style.right;
    if (hiddenRegionRightString && hiddenRegionRightString.includes('%')) {
      const hiddenRegionRightPercentage = parseFloat(hiddenRegionRightString.replace('%', ''));
      timelineLeftPercentage = 100 - hiddenRegionRightPercentage;
    }
    const hiddenRegionLeftString = hiddenRegion.element.style.left;
    if (timelineLeftPercentage === null && hiddenRegionLeftString && hiddenRegionLeftString.includes('%')) {
      timelineLeftPercentage = parseFloat(hiddenRegionLeftString.replace('%', ''));
    }

    const timelineLeftPercentageString = timelineLeftPercentage !== null
      ? timelineLeftPercentage.toFixed(5) + '%'
      : '0px';
    const firstDivElement = timelineElement.querySelector('div') as HTMLElement;
    firstDivElement.style.marginLeft = timelineLeftPercentageString;
  }

  refreshTimelineOffset(): void {
    if (!this.state) {
      return;
    }
    if (this.state.hiddenRegion) {
      const waveSurferElement = this.state.hiddenRegion.element.parentNode?.parentNode;
      this.state.timelineElement = waveSurferElement?.querySelector('[part="timeline"]') ?? this.state.timelineElement ?? null;
    }
    this.adjustTimelineMargin(this.state.hiddenRegion, this.state.timelineElement ?? null);
  }

  updateStartOffset(offset: number): void {
    if (!this.state) {
      return;
    }
    this.state.timelineOffsetReference = offset;
    this.state.currentStartOffset = offset;
    this.updateTimelinePluginOffset();
    this.emitViewState();
  }

  handleOffsetChange(offsetEnabled: boolean): void {
    if (!this.state) {
      return;
    }
    const state = this.state;
    if (offsetEnabled) {
      // 「開始直前の音を表示」= オフセットを外し、CD(S)を0秒にする
      state.currentStartOffset = 0;
      this.removeHiddenRegion();
      this.addRsstStartMarker();
      this.updateTimelinePluginOffset();
      this.emitViewState();
      this.refreshTimelineOffset();
      return;
    }

    // 非表示= オフセットを適用し、RSST STARTを0秒にする
    state.currentStartOffset = state.timelineOffsetReference;
    this.removeRsstStartMarker();
    this.createHiddenRegion();
    this.ensureWavePosition();
    this.updateTimelinePluginOffset();
    this.emitViewState();
    this.refreshTimelineOffset();
  }

  applyDefaultOffsetState(): void {
    if (!this.state) {
      return;
    }
    if (this.state.timelineOffsetReference <= 0) {
      this.removeHiddenRegion();
      this.removeRsstStartMarker();
      this.updateTimelinePluginOffset();
      this.emitViewState();
      this.refreshTimelineOffset();
      return;
    }
    // 初期状態: 開始直前の音を非表示（オフセット適用）にしておく
    this.state.currentStartOffset = this.state.timelineOffsetReference;
    this.removeRsstStartMarker();
    this.createHiddenRegion();
    this.ensureWavePosition();
    this.updateTimelinePluginOffset();
    this.emitViewState();
    this.refreshTimelineOffset();
  }

  getCurrentStartOffset(): number {
    return this.state?.currentStartOffset ?? 0;
  }

  /** 内部状態のスナップショットを取得（読み取り専用） */
  getStateSnapshot(): OffsetState | null {
    return this.state;
  }

  private emitViewState(): void {
    if (!this.state) {
      return;
    }
    this.viewState.next({
      currentStartOffset: this.state.currentStartOffset,
      timelineOffsetReference: this.state.timelineOffsetReference,
    });
  }

  private createHiddenRegion(): void {
    if (!this.state?.waveSurferRegions) {
      return;
    }
    this.removeHiddenRegion();
    const params = {
      ...this.state.hiddenRegionParams,
      end: this.state.timelineOffsetReference,
    };
    this.state.hiddenRegionParams = params;
    this.state.hiddenRegion = this.state.waveSurferRegions.addRegion(params);
    if (this.state.hiddenRegion?.element) {
      const el = this.state.hiddenRegion.element;
      el.style.zIndex = '10';
      el.style.borderRight = '1px solid';
      const surfaceColor = getComputedStyle(document.documentElement)
        .getPropertyValue('--gokuri-surface').trim();
      if (surfaceColor) {
        el.style.backgroundColor = surfaceColor;
      }
    }
  }

  private removeHiddenRegion(): void {
    if (this.state?.hiddenRegion) {
      this.state.hiddenRegion.remove();
      this.state.hiddenRegion = null;
    }
  }

  private addRsstStartMarker(): void {
    if (!this.state) {
      return;
    }
    if (!this.state.waveSurferRegions) {
      return;
    }
    this.removeRsstStartMarker();
    this.state.rsstStartMarker = this.regionService.addMarker(
      this.state.timelineOffsetReference,
      '#00C2F1',
      'RSSTSTART',
      'bottom',
    );
  }

  private removeRsstStartMarker(): void {
    if (this.state?.rsstStartMarker) {
      this.regionService.removeMarker(this.state.rsstStartMarker);
      this.state.rsstStartMarker = null;
    }
  }

  private ensureWavePosition(): void {
    if (!this.state?.wavesurfer || !this.state.currentStartOffset) {
      return;
    }
    if (this.state.wavesurfer.getCurrentTime() < this.state.currentStartOffset) {
      this.state.wavesurfer.seekTo(this.state.currentStartOffset / this.state.wavesurfer.getDuration());
    }
  }

  /**
   * AvSync 用の timeline timeOffset。負値を入れると、wavesurfer のラベル時刻表示が
   * その絶対値ぶん前倒しになる（例: -0.75 → 原 0.75s 位置のラベルが "0:00" を表示）。
   * AvSync 無効時は 0。
   */
  private avSyncTimeOffsetSec = 0;

  setAvSyncTimeOffset(seconds: number): void {
    const next = Number.isFinite(seconds) ? seconds : 0;
    if (this.avSyncTimeOffsetSec === next) {
      return;
    }
    this.avSyncTimeOffsetSec = next;
    this.updateTimelinePluginOffset();
  }

  private updateTimelinePluginOffset(): void {
    if (!this.state?.timelinePlugin) {
      return;
    }
    // 通常は 0。AvSync 有効時は `-waveStartSec` を使い、ラベルの時刻原点を
    // 可視左端（再生可能領域の先頭）に合わせる。
    const offset = this.avSyncTimeOffsetSec;
    const plugin = this.state.timelinePlugin;
    // TimelinePluginはtimeOffsetを参照するため、両方にセットする
    if (plugin.options) {
      plugin.options.timeOffset = offset;
      plugin.options.offset = offset;
    }
    if (plugin.params) {
      plugin.params.timeOffset = offset;
      plugin.params.offset = offset;
    }

    if (typeof plugin.initTimeline === 'function') {
      plugin.initTimeline();
    } else if (typeof plugin.render === 'function') {
      plugin.render();
    }

    // タイムラインの実DOMに対するマージン調整で0秒位置もシフトさせる
    this.refreshTimelineOffset();
  }
}
